import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Typography,
  Alert,
  Paper,
  Switch,
  IconButton,
  Box,
  Checkbox,
  createFilterOptions,
} from '@mui/material';
import { Rule } from './types';
import { validateRuleQuantity } from './ruleQuantity';
import { GiftRuleType } from '../../../../models';
import { GiftValueType } from '../../../../models/giftValueType';
import { ROLETA_RESOURCE, RoletaStatus } from '../../../../models/roleta';
import { AsyncAutocomplete, CurrencyInput } from '../../../../components';
import { CoordinatesMapEditor } from '../../../../components/CoordinatesMapInput';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CasinoIcon from '@mui/icons-material/Casino';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { Status, Wrapper } from '@googlemaps/react-wrapper';
import GiftRuleSimulator from './GiftRuleSimulator';

// Linha fixa no topo da lista de postos: marca/desmarca todos de uma vez.
// Nunca entra no value; o onChange intercepta o clique nela.
const ALL_PLACES = { id: '__all__', name: 'Marcar todos' };
const filterPlaces = createFilterOptions<{ id: string; name: string; isPartner?: boolean }>();

// Inline autocomplete component for bolão selection inside the rule modal.
// Kept inline to avoid spawning a tiny standalone file.
const BolaoAutocomplete: React.FC<{
  value: { id: string; name: string } | null;
  onChange: (b: { id: string; name: string } | null) => void;
  error?: string;
}> = ({ value, onChange, error }) => (
  <AsyncAutocomplete
    resource="boloes"
    label="Bolão vinculado"
    value={value}
    onChange={(b: any) => onChange(b ? { id: b.id, name: b.name } : null)}
    getOptionLabel={(option: any) => option.name || ''}
    filters={{ status: ['draft', 'active', 'closed', 'result_pending'] }}
    required
    helperText={error || 'Escolha o bolão pelo nome — apenas bolões não-processados aparecem.'}
    renderOption={(props: any, option: any) => (
      <li {...props} key={option.id}>
        <SportsSoccerIcon sx={{ color: '#7869DF', mr: 1 }} />
        <span>
          {option.name}
          {option.homeTeamName && option.awayTeamName && (
            <span style={{ color: '#999', marginLeft: 8 }}>
              ({option.homeTeamName} × {option.awayTeamName})
            </span>
          )}
        </span>
      </li>
    )}
  />
);

// Autocomplete de roleta, no mesmo molde do de bolão logo acima. Roleta
// encerrada fica de fora: um brinde de prêmios ligado a ela nunca entregaria nada.
const RoletaAutocomplete: React.FC<{
  value: { id: string; name: string } | null;
  onChange: (roleta: { id: string; name: string } | null) => void;
  error?: string;
}> = ({ value, onChange, error }) => (
  <AsyncAutocomplete
    resource={ROLETA_RESOURCE}
    label="Roleta vinculada"
    value={value}
    onChange={(roleta: any) => onChange(roleta ? { id: roleta.id, name: roleta.name } : null)}
    getOptionLabel={(option: any) => option.name || ''}
    filters={{ status: [RoletaStatus.Draft, RoletaStatus.Active, RoletaStatus.Inactive] }}
    required
    helperText={error || 'Escolha a roleta pelo nome — apenas roletas não encerradas aparecem.'}
    renderOption={(props: any, option: any) => (
      <li {...props} key={option.id}>
        <CasinoIcon sx={{ color: '#7869DF', mr: 1 }} />
        <span>{option.name}</span>
      </li>
    )}
  />
);

interface RuleModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (rule: Rule) => void;
  fuels: Array<{ id: string; name: string }>;
  stations: Array<{ id: string; name: string; isPartner?: boolean }>;
  hasMultipleGroups?: boolean;
  hasPixPayment?: boolean;
  hasPointsExchange?: boolean;
  hasFillinRules?: boolean;
  hasUniqueCode?: boolean;
  chain?: any;
}

export const RuleModal: React.FC<RuleModalProps> = ({
  open,
  onClose,
  onSave,
  fuels,
  stations,
  hasMultipleGroups = false,
  hasPixPayment = false,
  hasPointsExchange = false,
  hasFillinRules = false,
  hasUniqueCode = false,
  chain,
}) => {
  const [view, setView] = useState<'form' | 'location'>('form');
  const [formData, setFormData] = useState<Rule>({
    type: GiftRuleType.fillin,
    groupId: 0,
    fuelIds: [],
    valueType: 'volume',
    value: 0,
    quantity: 1,
    stationIds: [],
    clubeCertoCheckPeriodDays: null,
    code: '',
    bolaoId: null,
    bolaoName: null,
    roletaId: null,
    roletaName: null,
    useManualCode: true,
    useQrCode: false,
    requireLocation: false,
    locationCoordinates: [],
    saleProductIds: [],
    productIds: [],
    saleProductNames: [],
    productNames: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const partnerIds = new Set(stations.filter((s) => s.isPartner).map((s) => s.id));

  useEffect(() => {
    if (!open) {
      setFormData({
        type: GiftRuleType.fillin,
        groupId: 0,
        fuelIds: [],
        valueType: 'volume',
        value: 0,
        quantity: 1,
        stationIds: [],
        clubeCertoCheckPeriodDays: null,
        code: '',
        bolaoId: null,
        bolaoName: null,
        roletaId: null,
        roletaName: null,
        useManualCode: true,
        useQrCode: false,
        requireLocation: false,
        locationCoordinates: [],
      });
      setErrors({});
      setView('form');
    }
  }, [open]);

  const handleChange = (field: keyof Rule, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTypeChange = (newType: string) => {
    setFormData((prev) => {
      // Trocar entre tipos dirigidos por evento (abastecimento ↔ compra) preserva
      // o que os dois usam. Zerar aqui fazia quem preenchesse valor ou locais
      // ANTES de escolher o tipo perder tudo sem nenhum aviso.
      const keepsSharedFields = GiftRuleType.isEventType(prev.type) && GiftRuleType.isEventType(newType);
      const isSale = GiftRuleType.isSaleType(newType);

      return {
        type: newType,
        groupId: 0,
        // Combustível não existe em regra de compra
        fuelIds: keepsSharedFields && !isSale ? prev.fuelIds : [],
        // Compra é sempre em reais — não existe "litro" numa venda de conveniência.
        // Troca por Pontos é a terceira unidade: o valor dela é um PREÇO EM PONTOS.
        // Sem este caso a regra caía no fallback 'volume' e as telas de revisão e
        // de detalhe mostravam "500.00 L" para um brinde que custa 500 pontos.
        valueType: newType === GiftRuleType.points_exchange
          ? GiftValueType.points
          : (isSale ? 'cents' : (keepsSharedFields ? prev.valueType : 'volume')),
        value: keepsSharedFields ? prev.value : 0,
        quantity: newType === GiftRuleType.fillin || newType === GiftRuleType.sale
          ? (keepsSharedFields ? (prev.quantity ?? 1) : 1)
          : undefined,
        // Parceiro (loja) não tem bomba: compra → abastecimento perde os parceiros,
        // senão a regra fica presa a um local onde nunca há abastecimento.
        stationIds: keepsSharedFields
          ? (isSale ? prev.stationIds : prev.stationIds.filter((id) => !partnerIds.has(id)))
          : [],
        clubeCertoCheckPeriodDays: newType === GiftRuleType.clube_certo ? 30 : null,
        code: '',
        bolaoId: null,
        bolaoName: null,
        roletaId: null,
        roletaName: null,
        useManualCode: true,
        useQrCode: false,
        requireLocation: false,
        locationCoordinates: [],
        // Filtro de produtos só sobrevive entre os dois tipos de compra
        saleProductIds: isSale && keepsSharedFields ? (prev.saleProductIds ?? []) : [],
        productIds: isSale && keepsSharedFields ? (prev.productIds ?? []) : [],
        saleProductNames: isSale && keepsSharedFields ? (prev.saleProductNames ?? []) : [],
        productNames: isSale && keepsSharedFields ? (prev.productNames ?? []) : [],
      };
    });
    setErrors({});
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.type) {
      newErrors.type = 'Tipo de regra é obrigatório';
    }

    if (formData.type === GiftRuleType.fillin) {
      if (formData.value < 0) {
        newErrors.value = 'Valor não pode ser negativo';
      }
    }

    // Quantidade só existe em regra de abastecimento e de compra (o campo apagado virava null)
    if (formData.type === GiftRuleType.fillin || formData.type === GiftRuleType.sale) {
      const quantityError = validateRuleQuantity(formData.quantity);
      if (quantityError) {
        newErrors.quantity = quantityError;
      }
    }

    if (formData.type === GiftRuleType.accumulated_fillin) {
      if (!formData.value || formData.value <= 0) {
        newErrors.value = 'Limite acumulado deve ser maior que zero';
      }
    }

    // Validar pix_payment
    if (formData.type === GiftRuleType.pix_payment) {
      if (!formData.value || formData.value <= 0) {
        newErrors.value = 'Valor do pagamento deve ser maior que zero';
      }
    }

    // Validar points_exchange: o valor é o PREÇO EM PONTOS da troca.
    // Ponto é inteiro em todo o resto do sistema (customer_balance,
    // transaction.value) e o backend recusa quebrado — barrar aqui evita a
    // viagem só pra receber "Invalid request payload input".
    if (formData.type === GiftRuleType.points_exchange) {
      if (!formData.value || formData.value <= 0) {
        newErrors.value = 'A quantidade de pontos deve ser maior que zero';
      } else if (!Number.isInteger(formData.value)) {
        newErrors.value = 'A quantidade de pontos deve ser um número inteiro';
      }
    }

    // Validar unique_code
    if (formData.type === GiftRuleType.unique_code) {
      // Código só é obrigatório quando o método de digitação está ativo
      if (formData.useManualCode ?? true) {
        const code = (formData.code || '').toUpperCase().trim();
        if (!code) {
          newErrors.code = 'Código é obrigatório';
        } else if (!/^[A-Z0-9]{3,32}$/.test(code)) {
          newErrors.code = 'Código deve ter 3 a 32 caracteres alfanuméricos (letras e números)';
        }
      }
      if (!formData.useManualCode && !formData.useQrCode) {
        newErrors.useManualCode = 'Ative pelo menos um método de resgate (digitação ou QR Code)';
      }
      if (formData.requireLocation && (!formData.locationCoordinates || formData.locationCoordinates.length < 3)) {
        newErrors.locationCoordinates = 'Defina ao menos 3 pontos no mapa para a área de resgate';
      }
    }

    // Validar regras de compra
    if (GiftRuleType.isSaleType(formData.type)) {
      const hasProductFilter =
        (formData.saleProductIds || []).length > 0 || (formData.productIds || []).length > 0;

      if ((formData.saleProductIds || []).length > 0 && (formData.productIds || []).length > 0) {
        newErrors.saleProductIds = 'Escolha produtos de venda OU produtos do catálogo, não os dois';
      }

      if (formData.type === GiftRuleType.accumulated_sale) {
        // Sem limiar não há acumulado — a regra nunca fecharia um ciclo
        if (!formData.value || formData.value <= 0) {
          newErrors.value = 'Valor acumulado deve ser maior que zero';
        }
      } else if ((!formData.value || formData.value <= 0) && !hasProductFilter) {
        // Sem valor E sem filtro, a regra dispararia em qualquer compra
        newErrors.value = 'Informe um valor mínimo ou selecione os produtos';
      }
    }

    // Validar prediction_winner: bolão obrigatório
    if (formData.type === GiftRuleType.prediction_winner) {
      if (!formData.bolaoId) {
        newErrors.bolaoId = 'Selecione o bolão vinculado';
      }
    }

    // Validar roleta_prize: roleta obrigatória
    if (formData.type === GiftRuleType.roleta_prize) {
      if (!formData.roletaId) {
        newErrors.roletaId = 'Selecione a roleta vinculada';
      }
    }

    if (GiftRuleType.requiresStations(formData.type)
        && (!formData.stationIds || formData.stationIds.length === 0)) {
      newErrors.stationIds = GiftRuleType.isSaleType(formData.type)
        ? 'Selecione pelo menos um posto ou parceiro'
        : 'Selecione pelo menos um posto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    // Impedir salvamento se Clube Certo for selecionado e existem múltiplos grupos
    if (cannotSelectClubeCerto) {
      setErrors({ type: 'Não é possível adicionar Clube Certo quando existem múltiplos grupos' });
      return;
    }

    // Impedir salvamento se Pix Payment for selecionado e existem múltiplos grupos
    if (cannotSelectPixPaymentDueToGroups) {
      setErrors({ type: 'Não é possível adicionar Pagamento por Pix quando existem múltiplos grupos' });
      return;
    }

    // Impedir salvamento se Pix Payment for selecionado e já existem regras de abastecimento
    if (cannotSelectPixPaymentDueToFillin) {
      setErrors({ type: 'Pagamento por Pix é exclusivo e não pode ser combinado com regras de abastecimento' });
      return;
    }

    // Impedir salvamento se tentar adicionar regra de abastecimento quando existe Pix Payment
    if (cannotAddFillinDueToPixPayment) {
      setErrors({ type: 'Não é possível adicionar regras de abastecimento quando existe uma regra de Pagamento por Pix' });
      return;
    }

    // Impedir salvamento se Clube Certo for selecionado mas a rede não tem as configurações
    if (clubeCertoConfigMissing) {
      setErrors({ type: 'A rede não possui as configurações do Clube Certo preenchidas' });
      return;
    }

    // Impedir adicionar Código Único quando há outras regras (exclusivo, igual Pix)
    if (cannotSelectUniqueCode) {
      setErrors({ type: 'A regra de Divulgação é exclusiva — remova outras regras primeiro.' });
      return;
    }

    // Impedir adicionar outras regras quando já há Código Único
    if (cannotAddOtherDueToUniqueCode) {
      setErrors({ type: 'Não é possível adicionar esta regra: já existe uma regra de Divulgação neste brinde.' });
      return;
    }

    // Impedir adicionar Troca por Pontos junto de outra regra (exclusiva, igual Divulgação)
    if (cannotSelectPointsExchange) {
      setErrors({ type: 'A regra de Troca por Pontos é exclusiva — remova as outras regras primeiro.' });
      return;
    }

    // Impedir adicionar outras regras quando já há Troca por Pontos
    if (cannotAddOtherDueToPointsExchange) {
      setErrors({ type: 'Não é possível adicionar esta regra: já existe uma regra de Troca por Pontos neste brinde.' });
      return;
    }

    if (validate()) {
      const payload: Rule = {
        ...formData,
        code: formData.code ? formData.code.toUpperCase().trim() : null,
      };
      onSave(payload);
      onClose();
    }
  };

  const isAccumulatedType = formData.type === GiftRuleType.accumulated_fillin;
  const isClubeCertoType = formData.type === GiftRuleType.clube_certo;
  const isPixPaymentType = formData.type === GiftRuleType.pix_payment;
  const isUniqueCodeType = formData.type === GiftRuleType.unique_code;
  const isPredictionWinnerType = formData.type === GiftRuleType.prediction_winner;
  // "Prêmio da Roleta": o brinde é a caixa de prêmios da roda. Não tem posto,
  // combustível nem valor — quem dispara é o giro do cliente.
  const isRoletaPrizeType = formData.type === GiftRuleType.roleta_prize;
  // Regras de cadastro não têm posto, combustível nem valor — a conquista sai
  // uma única vez, quando o cliente se cadastra ou completa o perfil.
  const isRegistrationType = GiftRuleType.isRegistrationType(formData.type);
  // "Troca por Pontos": compra paga em pontos, feita na tela do sorteio. Não tem
  // posto nem combustível — o valor é o PREÇO, não um limiar de abastecimento.
  const isPointsExchangeType = formData.type === GiftRuleType.points_exchange;
  const isSaleType = formData.type === GiftRuleType.sale;
  const isAccumulatedSaleType = formData.type === GiftRuleType.accumulated_sale;
  const isAnySaleType = isSaleType || isAccumulatedSaleType;
  // Parceiro (loja) só vale em regra de compra. Em regra de abastecimento ele
  // nunca casa — não tem bomba — e o brinde fica sem gerar nada (caso Michells, 10/2026).
  const placeOptions = isAnySaleType ? stations : stations.filter((s) => !s.isPartner);
  const selectedPlaces = placeOptions.filter((s) => formData.stationIds.includes(s.id));
  const allPlacesOn = placeOptions.length > 0 && selectedPlaces.length === placeOptions.length;
  // Regras dirigidas por evento — abastecimento e compra convivem entre si
  // (mesmo grupo = OU, grupos diferentes = E), mas nenhuma delas convive com
  // pix/unique_code/clube_certo/prediction_winner.
  const isFillinType = formData.type === GiftRuleType.fillin
    || formData.type === GiftRuleType.accumulated_fillin
    || isAnySaleType;
  // Exclusão mútua dos dois seletores de produto
  const hasSaleProductSelection = (formData.saleProductIds || []).length > 0;
  const hasProductSelection = (formData.productIds || []).length > 0;

  /**
   * Produto do catálogo só é identificado na venda através dos SKUs do combo
   * (product_bundle). Sem combo, a regra nunca casaria — o backend recusa na
   * criação e aqui a opção fica desabilitada.
   */
  const hasProductBundle = (product: any) => (product?.productBundles?.length ?? 0) > 0;
  const cannotSelectClubeCerto = hasMultipleGroups && isClubeCertoType;
  const cannotSelectPixPaymentDueToGroups = hasMultipleGroups && isPixPaymentType;

  // REGRA DE EXCLUSIVIDADE: pix_payment não pode coexistir com regras de abastecimento
  const cannotSelectPixPaymentDueToFillin = hasFillinRules && isPixPaymentType;
  const cannotAddFillinDueToPixPayment = hasPixPayment && isFillinType;
  const cannotSelectPixPayment = cannotSelectPixPaymentDueToGroups || cannotSelectPixPaymentDueToFillin;

  // REGRA DE EXCLUSIVIDADE: points_exchange é compra, e o preço é do brinde
  // INTEIRO — qualquer outra regra mudaria o que está sendo vendido ("ganha de
  // graça OU pagando"). Mesmo tratamento do unique_code, e é o que o backend
  // recusa em gifts-controller ("não pode ser combinada com outras regras").
  const cannotSelectPointsExchangeDueToGroups = hasMultipleGroups && isPointsExchangeType;
  const cannotSelectPointsExchangeDueToOthers = (hasFillinRules || hasPixPayment || hasUniqueCode) && isPointsExchangeType;
  const cannotSelectPointsExchange = cannotSelectPointsExchangeDueToGroups || cannotSelectPointsExchangeDueToOthers;
  const cannotAddOtherDueToPointsExchange = hasPointsExchange && !isPointsExchangeType;

  // REGRA DE EXCLUSIVIDADE: unique_code é ferramenta de atribuição/marketing
  // Não coexiste com fillin/accumulated/pix/clube_certo, single group only.
  const cannotSelectUniqueCodeDueToFillin = (hasFillinRules || hasPixPayment) && isUniqueCodeType;
  const cannotSelectUniqueCodeDueToGroups = hasMultipleGroups && isUniqueCodeType;
  const cannotSelectUniqueCode = cannotSelectUniqueCodeDueToFillin || cannotSelectUniqueCodeDueToGroups;
  const cannotAddOtherDueToUniqueCode = hasUniqueCode && !isUniqueCodeType;

  // Verificar se a rede tem as configurações de Clube Certo preenchidas
  const hasClubeCertoConfig = chain?.hasClubeCertoConfig === true;
  const clubeCertoConfigMissing = isClubeCertoType && !hasClubeCertoConfig;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {view === 'location' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setView('form')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" fontWeight={600}>Definir área de resgate</Typography>
              <Typography variant="caption" color="text.secondary">
                Polígono com 3 a 4 pontos — clique no mapa para adicionar
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="h6" fontWeight={600}>Adicionar Nova Regra</Typography>
            <Typography variant="caption" color="text.secondary">
              Configure os critérios para esta regra
            </Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {view === 'location' ? (
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clique no mapa para definir a área de resgate (mínimo 3 pontos, máximo 4).
              Clique em um marcador numerado para removê-lo.
            </Typography>
            <Wrapper
              apiKey={(import.meta as any).env?.VITE_MAP_API_KEY || 'AIzaSyA2-gtjUXGbaFwbFy8pmjTPgoV4pTLbx2g'}
              libraries={['marker'] as any}
              render={(status: any) => {
                if (status === Status.LOADING) return <Typography>Carregando mapa...</Typography>;
                if (status === Status.FAILURE) return <Typography color="error">Erro ao carregar mapa</Typography>;
                return null;
              }}
            >
              <CoordinatesMapEditor
                coordinates={formData.locationCoordinates || []}
                onChange={(coords: Array<{ latitude: number; longitude: number }>) =>
                  handleChange('locationCoordinates', coords)
                }
                maxPoints={4}
              />
            </Wrapper>
          </Box>
        ) : (
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Tipo de Regra - Full Width */}
          <Grid size={12}>
            <FormControl fullWidth error={!!errors.type}>
              <InputLabel>Tipo de Regra</InputLabel>
              <Select
                value={formData.type}
                label="Tipo de Regra"
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {GiftRuleType.giftRuleTypeChoices
                  .filter((choice) => !(choice.id === GiftRuleType.prediction_winner && !chain?.useBolao))
                  .filter((choice) => !(choice.id === GiftRuleType.roleta_prize && !chain?.useRoleta))
                  // Troca por Pontos NÃO é gateada por módulo: ela paga qualquer
                  // recompensa do Clube, e não só o número da sorte. Gatear por
                  // useDrawing era o mesmo acoplamento que o backend desfez.
                  .map((choice) => (
                    <MenuItem key={choice.id} value={choice.id}>
                      {choice.name}
                    </MenuItem>
                  ))}
              </Select>
              {errors.type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {errors.type}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Caixa informativa para Abastecimentos Acumulados */}
          {isAccumulatedType && (
            <Grid size={12}>
              <Alert
                severity="info"
                sx={{
                  bgcolor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  '& .MuiAlert-icon': { color: '#2196f3' }
                }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  Como funcionam os Abastecimentos Acumulados?
                </Typography>
                <Typography variant="body2">
                  Neste tipo de regra, o sistema <strong>soma o valor de todos os abastecimentos</strong> realizados pelo cliente até atingir o limite configurado.
                  Diferente do abastecimento único, não importa quantas vezes o cliente abasteceu, mas sim o <strong>valor total acumulado</strong> em litros ou reais.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Exemplo:</strong> Configure um limite de 100 litros. O cliente pode abastecer 30L, depois 40L, depois 30L.
                  Quando a soma atingir 100L, ele ganha o brinde.
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Caixa informativa para Clube Certo */}
          {isClubeCertoType && (
            <>
              <Grid size={12}>
                <Alert
                  severity={(cannotSelectClubeCerto || clubeCertoConfigMissing) ? "error" : "info"}
                  sx={{
                    bgcolor: (cannotSelectClubeCerto || clubeCertoConfigMissing) ? '#ffebee' : '#e3f2fd',
                    border: (cannotSelectClubeCerto || clubeCertoConfigMissing) ? '1px solid #f44336' : '1px solid #2196f3',
                    '& .MuiAlert-icon': { color: (cannotSelectClubeCerto || clubeCertoConfigMissing) ? '#f44336' : '#2196f3' }
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {cannotSelectClubeCerto
                      ? "⚠️ Não é Possível Adicionar Esta Regra"
                      : clubeCertoConfigMissing
                        ? "⚠️ Configurações do Clube Certo Não Preenchidas"
                        : "ℹ️ Regra Exclusiva: Clube Certo"}
                  </Typography>
                  {cannotSelectClubeCerto ? (
                    <Typography variant="body2">
                      A regra <strong>"Clube Certo"</strong> é uma regra exclusiva e <strong>não pode ser adicionada quando existem múltiplos grupos</strong>.
                      Remova os grupos extras para poder usar esta regra, ou escolha outro tipo de regra.
                    </Typography>
                  ) : clubeCertoConfigMissing ? (
                    <Typography variant="body2">
                      A rede não possui as <strong>configurações do Clube Certo</strong> preenchidas (Login e Senha).
                      Acesse a página de edição da rede, vá até a aba <strong>"Integrações"</strong> e preencha os campos de Clube Certo antes de criar esta regra.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">
                        A regra <strong>"Clube Certo"</strong> é uma regra exclusiva e <strong>não pode ser combinada com outros grupos usando E TAMBÉM</strong>.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Você ainda pode adicionar outras regras no mesmo grupo usando <strong>OU</strong>, mas não poderá criar grupos adicionais enquanto esta regra estiver ativa.
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>

              {/* Configuração do Clube Certo */}
              {!cannotSelectClubeCerto && !clubeCertoConfigMissing && (
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                    Configuração do Clube Certo
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
                      Período de Verificação
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Período de Verificação (dias)"
                          type="number"
                          value={formData.clubeCertoCheckPeriodDays || 30}
                          onChange={(e) => handleChange('clubeCertoCheckPeriodDays', parseInt(e.target.value) || 30)}
                          error={!!errors.clubeCertoCheckPeriodDays}
                          helperText={errors.clubeCertoCheckPeriodDays || 'Intervalo em dias para verificação no Clube Certo'}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          )}

          {/* Caixa informativa para as regras de cadastro */}
          {isRegistrationType && (
            <Grid size={12}>
              <Alert
                severity="info"
                sx={{
                  bgcolor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  '& .MuiAlert-icon': { color: '#2196f3' }
                }}
              >
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {formData.type === GiftRuleType.customer_registered
                    ? 'Como funciona "Fez o cadastro"?'
                    : 'Como funciona "Completou o cadastro"?'}
                </Typography>
                <Typography variant="body2">
                  {formData.type === GiftRuleType.customer_registered
                    ? 'O cliente ganha o brinde quando confirma o cadastro no aplicativo.'
                    : 'O cliente ganha o brinde quando termina de preencher os dados do perfil no aplicativo.'}
                  {' '}Não depende de abastecimento, compra nem posto — por isso esta regra não tem
                  combustível, valor nem seleção de postos.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Atenção:</strong> a conquista sai <strong>uma única vez por cliente</strong>, para sempre.
                </Typography>
              </Alert>
            </Grid>
          )}

          {/* Formulário para Abastecimento Único (fillin) */}
          {!isAccumulatedType && !isClubeCertoType && !isUniqueCodeType && !isPredictionWinnerType && !isRoletaPrizeType && !isRegistrationType && !isPointsExchangeType && !isAnySaleType && formData.type !== GiftRuleType.pix_payment && (
            <>
              {/* Alerta quando Pix Payment já existe */}
              {cannotAddFillinDueToPixPayment && (
                <Grid size={12}>
                  <Alert
                    severity="error"
                    sx={{
                      bgcolor: '#ffebee',
                      border: '1px solid #f44336',
                      '& .MuiAlert-icon': { color: '#f44336' }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      ⚠️ Não é Possível Adicionar Esta Regra
                    </Typography>
                    <Typography variant="body2">
                      Já existe uma regra de <strong>"Pagamento por Pix"</strong> configurada neste brinde.
                      Regras de abastecimento <strong>não podem ser combinadas</strong> com regras de pagamento por Pix.
                      Remova a regra de Pagamento por Pix primeiro, ou escolha outro brinde para regras de abastecimento.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                  Configuração de Abastecimento Único
                </Typography>
              </Grid>

              <Grid size={12}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
                    Configuração de Limite
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <Autocomplete
                        multiple
                        options={fuels}
                        getOptionLabel={(f) => f.name}
                        value={fuels.filter(f => (formData.fuelIds || []).includes(f.id))}
                        onChange={(_, newValue) =>
                          handleChange('fuelIds', newValue.map(f => f.id))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Combustível"
                            placeholder={(formData.fuelIds || []).length === 0 ? 'Todos' : ''}
                          />
                        )}
                        noOptionsText="Nenhum combustível encontrado"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo do Limite</InputLabel>
                        <Select
                          value={formData.valueType}
                          label="Tipo do Limite"
                          onChange={(e) => handleChange('valueType', e.target.value)}
                        >
                          <MenuItem value="volume">Litros</MenuItem>
                          <MenuItem value="cents">Reais</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      {formData.valueType === 'cents' ? (
                        <CurrencyInput
                          fullWidth
                          label="Valor Mínimo"
                          value={formData.value}
                          onChange={(value) => handleChange('value', value)}
                          error={!!errors.value}
                          helperText={errors.value || 'Valor mínimo em reais'}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Litros Mínimos"
                          type="number"
                          value={formData.value}
                          onChange={(e) => handleChange('value', parseFloat(e.target.value) || 0)}
                          error={!!errors.value}
                          helperText={errors.value || 'Volume mínimo em litros'}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      )}
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        label="Quantidade"
                        type="number"
                        value={formData.quantity ?? ''}
                        onChange={(e) => handleChange('quantity', e.target.value === '' ? null : parseInt(e.target.value))}
                        error={!!errors.quantity}
                        helperText={errors.quantity || 'Precisará abastecer X vezes'}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </>
          )}

          {/* Formulário para regras de Compra (sale / accumulated_sale) */}
          {isAnySaleType && (
            <>
              {cannotAddFillinDueToPixPayment && (
                <Grid size={12}>
                  <Alert severity="error" sx={{ bgcolor: '#ffebee', border: '1px solid #f44336', '& .MuiAlert-icon': { color: '#f44336' } }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      ⚠️ Não é Possível Adicionar Esta Regra
                    </Typography>
                    <Typography variant="body2">
                      Já existe uma regra de <strong>"Pagamento por Pix"</strong> configurada neste brinde.
                      Regras de compra <strong>não podem ser combinadas</strong> com regras de pagamento por Pix.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid size={12}>
                <Alert severity="info" sx={{ bgcolor: '#e3f2fd', border: '1px solid #2196f3', '& .MuiAlert-icon': { color: '#2196f3' } }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {isAccumulatedSaleType ? 'Como funcionam as Compras Acumuladas?' : 'Como funciona a regra de Compra?'}
                  </Typography>
                  <Typography variant="body2">
                    {isAccumulatedSaleType ? (
                      <>O sistema <strong>soma o valor das compras</strong> do cliente até atingir o valor configurado.
                      Só contam os <strong>produtos</strong> da venda — combustível nunca entra nessa conta.</>
                    ) : (
                      <>A regra olha o valor dos <strong>produtos</strong> de uma compra — combustível nunca entra nessa conta.
                      Se você não escolher nenhum produto abaixo, vale a venda inteira de produtos.</>
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Exemplo:</strong>{' '}
                    {isAccumulatedSaleType
                      ? 'gaste R$ 100,00 na conveniência ao longo do mês e ganhe o brinde.'
                      : 'compre R$ 30,00 ou mais na conveniência e ganhe R$ 0,10 de desconto por litro.'}
                  </Typography>
                </Alert>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                  {isAccumulatedSaleType ? 'Configuração de Compras Acumuladas' : 'Configuração de Compra'}
                </Typography>
              </Grid>

              <Grid size={12}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
                    Valor
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: isAccumulatedSaleType ? 12 : 6 }}>
                      <CurrencyInput
                        fullWidth
                        label={isAccumulatedSaleType ? 'Valor Acumulado' : 'Valor Mínimo da Compra'}
                        value={formData.value || 0}
                        onChange={(value) => handleChange('value', value)}
                        error={!!errors.value}
                        helperText={
                          errors.value
                          || (isAccumulatedSaleType
                            ? 'Soma total das compras em reais'
                            // O campo nunca fica "vazio" — fica R$ 0,00. Dizer isso
                            // explicitamente, senão não há como distinguir "não quis
                            // valor" de "o valor se perdeu".
                            : !formData.value
                              ? '⚠️ R$ 0,00 = vale qualquer compra dos produtos selecionados'
                              : 'Valor mínimo da compra, somando só os produtos')
                        }
                      />
                    </Grid>

                    {!isAccumulatedSaleType && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label="Quantidade"
                          type="number"
                          value={formData.quantity ?? ''}
                          onChange={(e) => handleChange('quantity', e.target.value === '' ? null : parseInt(e.target.value))}
                          error={!!errors.quantity}
                          helperText={errors.quantity || 'Precisará comprar X vezes'}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  <Typography variant="subtitle2" fontWeight={600} mt={3} mb={0.5} color="text.secondary">
                    Produtos (opcional)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Sem nenhuma seleção, vale a venda inteira de produtos. Escolha por
                    produto de venda <strong>ou</strong> por produto do catálogo — um desabilita o outro.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AsyncAutocomplete
                        multiple
                        resource="chains/chainId/sale-products"
                        label="Produtos de venda (PDV)"
                        value={formData.saleProductNames || []}
                        onChange={(value: any) => {
                          const selected = (value || []) as Array<{ id: string; name: string }>;
                          handleChange('saleProductNames', selected);
                          handleChange('saleProductIds', selected.map(s => s.id));
                        }}
                        getOptionLabel={(option: any) => option.name || ''}
                        disabled={hasProductSelection}
                        helperText={
                          hasProductSelection
                            ? 'Limpe os produtos do catálogo para usar este campo'
                            : 'Códigos do PDV — identificados direto na venda'
                        }
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                      <AsyncAutocomplete
                        multiple
                        resource="chains/chainId/products"
                        label="Produtos do catálogo"
                        value={formData.productNames || []}
                        onChange={(value: any) => {
                          const selected = (value || []) as Array<{ id: string; name: string }>;
                          handleChange('productNames', selected);
                          handleChange('productIds', selected.map(s => s.id));
                        }}
                        getOptionLabel={(option: any) => option.name || ''}
                        disabled={hasSaleProductSelection}
                        // Um produto do catálogo só é reconhecido na venda pelos
                        // itens do combo. Sem combo a regra ficaria inerte — o
                        // backend recusa, então bloqueamos já na escolha em vez de
                        // deixar o erro aparecer só ao salvar o brinde.
                        getOptionDisabled={(option: any) => !hasProductBundle(option)}
                        renderOption={(props: any, option: any) => {
                          const semCombo = !hasProductBundle(option);
                          return (
                            <li {...props} key={option.id}>
                              <Box>
                                <Typography variant="body2">{option.name}</Typography>
                                {semCombo && (
                                  <Typography variant="caption" color="text.secondary">
                                    Sem combo cadastrado — não pode ser usado aqui
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        helperText={
                          hasSaleProductSelection
                            ? 'Limpe os produtos de venda para usar este campo'
                            : 'Só aparecem habilitados os produtos com combo cadastrado'
                        }
                      />
                    </Grid>

                    {errors.saleProductIds && (
                      <Grid size={12}>
                        <Typography variant="caption" color="error">{errors.saleProductIds}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </>
          )}

          {/* Formulário para Abastecimentos Acumulados (accumulated_fillin) */}
          {isAccumulatedType && (
            <>
              {/* Alerta quando Pix Payment já existe */}
              {cannotAddFillinDueToPixPayment && (
                <Grid size={12}>
                  <Alert
                    severity="error"
                    sx={{
                      bgcolor: '#ffebee',
                      border: '1px solid #f44336',
                      '& .MuiAlert-icon': { color: '#f44336' }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      ⚠️ Não é Possível Adicionar Esta Regra
                    </Typography>
                    <Typography variant="body2">
                      Já existe uma regra de <strong>"Pagamento por Pix"</strong> configurada neste brinde.
                      Regras de abastecimento acumulado <strong>não podem ser combinadas</strong> com regras de pagamento por Pix.
                      Remova a regra de Pagamento por Pix primeiro, ou escolha outro brinde para regras de abastecimento.
                    </Typography>
                  </Alert>
                </Grid>
              )}

              <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                  Configuração de Abastecimentos Acumulados
                </Typography>
              </Grid>

              <Grid size={12}>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2} color="text.secondary">
                    Configuração de Limite Acumulado
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Autocomplete
                        multiple
                        options={fuels}
                        getOptionLabel={(f) => f.name}
                        value={fuels.filter(f => (formData.fuelIds || []).includes(f.id))}
                        onChange={(_, newValue) =>
                          handleChange('fuelIds', newValue.map(f => f.id))
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Combustível"
                            placeholder={(formData.fuelIds || []).length === 0 ? 'Todos' : ''}
                          />
                        )}
                        noOptionsText="Nenhum combustível encontrado"
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo do Limite</InputLabel>
                        <Select
                          value={formData.valueType || 'volume'}
                          label="Tipo do Limite"
                          onChange={(e) => handleChange('valueType', e.target.value)}
                        >
                          <MenuItem value="volume">Litros</MenuItem>
                          <MenuItem value="cents">Reais</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      {formData.valueType === 'cents' ? (
                        <CurrencyInput
                          fullWidth
                          label="Limite Acumulado"
                          value={formData.value || 0}
                          onChange={(value) => handleChange('value', value)}
                          error={!!errors.value}
                          helperText={errors.value || 'Soma total em reais'}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          label="Limite Acumulado"
                          type="number"
                          value={formData.value || 0}
                          onChange={(e) => handleChange('value', parseFloat(e.target.value) || 0)}
                          error={!!errors.value}
                          helperText={errors.value || 'Soma total em litros'}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </>
          )}

          {/* Formulário para Pagamento por Pix (pix_payment) */}
          {isPixPaymentType && (
            <>
              <Grid size={12}>
                <Alert
                  severity={cannotSelectPixPayment ? "error" : "info"}
                  sx={{
                    bgcolor: cannotSelectPixPayment ? '#ffebee' : '#e8f5e9',
                    border: cannotSelectPixPayment ? '1px solid #f44336' : '1px solid #4caf50',
                    '& .MuiAlert-icon': { color: cannotSelectPixPayment ? '#f44336' : '#4caf50' }
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {cannotSelectPixPayment
                      ? "⚠️ Não é Possível Adicionar Esta Regra"
                      : "💳 Pagamento por Pix - Venda de Descontos"}
                  </Typography>
                  {cannotSelectPixPaymentDueToFillin ? (
                    <Typography variant="body2">
                      A regra <strong>"Pagamento por Pix"</strong> é uma regra <strong>EXCLUSIVA</strong> e <strong>não pode ser combinada com regras de abastecimento</strong>.
                      Remova as regras de abastecimento existentes para poder usar esta regra, ou escolha outro tipo de regra.
                    </Typography>
                  ) : cannotSelectPixPaymentDueToGroups ? (
                    <Typography variant="body2">
                      A regra <strong>"Pagamento por Pix"</strong> é uma regra exclusiva e <strong>não pode ser adicionada quando existem múltiplos grupos</strong>.
                      Remova os grupos extras para poder usar esta regra, ou escolha outro tipo de regra.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">
                        Com esta regra, o cliente <strong>paga um valor via Pix</strong> para adquirir os descontos/produtos configurados neste brinde.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Exemplo:</strong> Configure o valor de R$ 9,99 e adicione 3 descontos de R$ 0,20/litro como produtos.
                        O cliente paga via Pix e ganha os 3 descontos automaticamente.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                        <strong>Atenção:</strong> Esta regra é exclusiva e não pode ser combinada com regras de abastecimento.
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>

              {!cannotSelectPixPayment && (
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                    Configuração do Pagamento
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <CurrencyInput
                          fullWidth
                          label="Valor do Pagamento"
                          value={formData.value || 0}
                          onChange={(value) => handleChange('value', value)}
                          error={!!errors.value}
                          helperText={errors.value || 'Valor que o cliente pagará via Pix'}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          )}

          {/* Formulário para Troca por Pontos (points_exchange) */}
          {isPointsExchangeType && (
            <>
              <Grid size={12}>
                <Alert
                  severity={cannotSelectPointsExchange ? "error" : "info"}
                  sx={{
                    bgcolor: cannotSelectPointsExchange ? '#ffebee' : '#e0f2f1',
                    border: cannotSelectPointsExchange ? '1px solid #f44336' : '1px solid #00897b',
                    '& .MuiAlert-icon': { color: cannotSelectPointsExchange ? '#f44336' : '#00897b' }
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {cannotSelectPointsExchange
                      ? "⚠️ Não é Possível Adicionar Esta Regra"
                      : "🎟️ Troca por Pontos — o Cliente Compra a Recompensa"}
                  </Typography>
                  {cannotSelectPointsExchangeDueToGroups ? (
                    <Typography variant="body2">
                      A regra <strong>"Troca por Pontos"</strong> é exclusiva e <strong>não pode ser usada quando existem múltiplos grupos</strong>. Remova os grupos extras primeiro.
                    </Typography>
                  ) : cannotSelectPointsExchangeDueToOthers ? (
                    <Typography variant="body2">
                      A regra <strong>"Troca por Pontos"</strong> é <strong>EXCLUSIVA</strong>: o preço é do brinde inteiro. Remova as outras regras deste brinde para poder usá-la.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">
                        O cliente <strong>paga em pontos</strong> e recebe <strong>na hora</strong> as recompensas deste brinde — <strong>quaisquer que sejam</strong>: cupom, desconto de combustível, número da sorte, giro na roleta ou direito ao palpite.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Exemplo:</strong> 500 pontos por troca. O cliente com saldo aperta "Trocar", os pontos saem do saldo e a recompensa entra na conta dele no mesmo instante.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Onde ele troca:</strong> na tela do brinde, no Clube de Vantagens. Quando a recompensa é Número da Sorte, o botão também aparece na tela do sorteio.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                        <strong>Atenção:</strong> o brinde precisa ter pelo menos uma recompensa — sem ela não há o que entregar e o cadastro é recusado. A <strong>quantidade</strong> de cada recompensa é configurada na aba Recompensas, não aqui.
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>

              {!cannotSelectPointsExchange && (
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                    Preço da Troca
                  </Typography>
                  <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        {/* Pontos, não dinheiro: nada de CurrencyInput aqui — ele
                            trabalha em centavos e produziria preço quebrado. */}
                        <TextField
                          fullWidth
                          type="number"
                          label="Pontos por troca"
                          value={formData.value || ''}
                          onChange={(e) => handleChange('value', parseInt(e.target.value, 10) || 0)}
                          error={!!errors.value}
                          helperText={errors.value || 'Quantos pontos o cliente paga a cada troca. Número inteiro.'}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          )}

          {/* Formulário para Código Único (unique_code) - canal de aquisição/atribuição */}
          {isUniqueCodeType && (
            <>
              <Grid size={12}>
                <Alert
                  severity={cannotSelectUniqueCode ? "error" : "info"}
                  sx={{
                    bgcolor: cannotSelectUniqueCode ? '#ffebee' : '#e3f2fd',
                    border: cannotSelectUniqueCode ? '1px solid #f44336' : '1px solid #2196f3',
                    '& .MuiAlert-icon': { color: cannotSelectUniqueCode ? '#f44336' : '#2196f3' }
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {cannotSelectUniqueCode
                      ? "⚠️ Não é Possível Adicionar Esta Regra"
                      : "🎟️ Divulgação — Ferramenta de Atribuição"}
                  </Typography>
                  {cannotSelectUniqueCodeDueToFillin ? (
                    <Typography variant="body2">
                      A regra de <strong>Divulgação</strong> é exclusiva e <strong>não pode coexistir</strong> com regras de abastecimento ou pagamento por Pix. Remova as outras primeiro.
                    </Typography>
                  ) : cannotSelectUniqueCodeDueToGroups ? (
                    <Typography variant="body2">
                      A regra de <strong>Divulgação</strong> não pode ser usada quando existem múltiplos grupos. Remova os grupos extras primeiro.
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2">
                        Use esta regra para campanhas de divulgação externa (rádio, parceiro, SMS, evento, influencer). O brinde <strong>não aparece</strong> na lista do Clube de Vantagens — o cliente só resgata pelo método configurado abaixo.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'warning.main' }}>
                        <strong>Atenção:</strong> limite fixo de 1 resgate por cliente. Vigência controlada pela agenda do brinde (data início/fim).
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>

              {!cannotSelectUniqueCode && (
                <>
                  {/* Métodos de Resgate */}
                  <Grid size={12}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                      Métodos de Resgate
                    </Typography>
                    {errors.useManualCode && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                        {errors.useManualCode}
                      </Typography>
                    )}
                  </Grid>

                  {/* Seção: Código Único (digitação) */}
                  <Grid size={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>Código Único</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Cliente digita o código recebido no app
                          </Typography>
                        </Box>
                        <Switch
                          checked={formData.useManualCode ?? true}
                          onChange={(e) => handleChange('useManualCode', e.target.checked)}
                        />
                      </Box>
                      {(formData.useManualCode ?? true) && (
                        <Box sx={{ mt: 2 }}>
                          <TextField
                            fullWidth
                            label="Código de Divulgação"
                            value={formData.code || ''}
                            onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                            error={!!errors.code}
                            helperText={errors.code || '3 a 32 caracteres, letras e números (ex: ITATIAIA26, RADIO2026, PROMO10)'}
                            inputProps={{
                              maxLength: 32,
                              style: { textTransform: 'uppercase', fontFamily: 'monospace', letterSpacing: '2px' },
                            }}
                          />
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Seção: QR Code */}
                  <Grid size={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>QR Code</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Cliente lê o QR Code com a câmera do app
                          </Typography>
                        </Box>
                        <Switch
                          checked={formData.useQrCode ?? false}
                          onChange={(e) => handleChange('useQrCode', e.target.checked)}
                        />
                      </Box>
                      {(formData.useQrCode ?? false) && (
                        <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
                          <Typography variant="caption">
                            O QR Code será gerado automaticamente após salvar o brinde. Você poderá visualizá-lo e fazer download na tela de detalhes.
                          </Typography>
                        </Alert>
                      )}
                    </Paper>
                  </Grid>

                  {/* Validações de Resgate */}
                  <Grid size={12}>
                    <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom sx={{ mt: 1 }}>
                      Validações de Resgate
                    </Typography>
                  </Grid>

                  {/* Seção: Localização */}
                  <Grid size={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: 'grey.50',
                        border: '1px solid',
                        borderColor: errors.locationCoordinates ? 'error.main' : 'grey.300',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>Localização</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Verificar posição GPS do cliente no momento do resgate
                          </Typography>
                        </Box>
                        <Switch
                          checked={formData.requireLocation ?? false}
                          onChange={(e) => handleChange('requireLocation', e.target.checked)}
                        />
                      </Box>
                      {formData.requireLocation && (
                        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MapOutlinedIcon />}
                            onClick={() => setView('location')}
                          >
                            {formData.locationCoordinates && formData.locationCoordinates.length >= 3
                              ? 'Editar área no mapa'
                              : 'Definir área no mapa'}
                          </Button>
                          {formData.locationCoordinates && formData.locationCoordinates.length >= 3 ? (
                            <Typography variant="caption" color="success.main">
                              ✓ {formData.locationCoordinates.length} pontos definidos
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Nenhuma área definida ainda
                            </Typography>
                          )}
                        </Box>
                      )}
                      {errors.locationCoordinates && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {errors.locationCoordinates}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                </>
              )}
            </>
          )}

          {/* Formulário para Acertou Bolão (prediction_winner) */}
          {isPredictionWinnerType && (
            <>
              <Grid size={12}>
                <Alert
                  severity="info"
                  sx={{
                    bgcolor: '#ede9fe',
                    border: '1px solid #7869DF',
                    '& .MuiAlert-icon': { color: '#7869DF' },
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    ⚽ Acertou Bolão — Prêmio para Quem Acertar
                  </Typography>
                  <Typography variant="body2">
                    Esta regra entrega o brinde apenas para clientes que acertarem o <strong>placar exato</strong> de um bolão que você já cadastrou.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    <strong>Atenção:</strong> esta regra NÃO dispara automaticamente por abastecimento. Ela só é processada quando você abrir o bolão, cadastrar o placar oficial e clicar em <strong>"Aplicar Recompensas"</strong>.
                  </Typography>
                </Alert>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                  Bolão Vinculado
                </Typography>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <BolaoAutocomplete
                    value={formData.bolaoId && formData.bolaoName ? { id: formData.bolaoId, name: formData.bolaoName } : null}
                    onChange={(b) => {
                      handleChange('bolaoId', b?.id || null);
                      handleChange('bolaoName', b?.name || null);
                    }}
                    error={errors.bolaoId}
                  />
                </Paper>
              </Grid>
            </>
          )}

          {/* Formulário para Prêmio da Roleta (roleta_prize) */}
          {isRoletaPrizeType && (
            <>
              <Grid size={12}>
                <Alert
                  severity="info"
                  sx={{
                    bgcolor: '#ede9fe',
                    border: '1px solid #7869DF',
                    '& .MuiAlert-icon': { color: '#7869DF' },
                  }}
                >
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    🎡 Prêmio da Roleta
                  </Typography>
                  <Typography variant="body2">
                    Este brinde guarda os <strong>prêmios das fatias</strong> da roleta escolhida. Cada fatia de prêmio
                    aponta para uma das recompensas cadastradas aqui.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    <strong>Atenção:</strong> esta regra NÃO dispara por abastecimento. A entrega acontece no instante
                    em que o cliente gira a roleta e a roda para numa fatia de prêmio.
                  </Typography>
                </Alert>
              </Grid>

              <Grid size={12}>
                <Typography variant="subtitle1" fontWeight={600} color="primary" gutterBottom>
                  Roleta Vinculada
                </Typography>
                <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <RoletaAutocomplete
                    value={formData.roletaId && formData.roletaName ? { id: formData.roletaId, name: formData.roletaName } : null}
                    onChange={(roleta) => {
                      handleChange('roletaId', roleta?.id || null);
                      handleChange('roletaName', roleta?.name || null);
                    }}
                    error={errors.roletaId}
                  />
                </Paper>
              </Grid>
            </>
          )}

          {/* Seleção de Postos - Comum para abastecimentos */}
          {GiftRuleType.requiresStations(formData.type) && (
            <Grid size={12}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                limitTags={5}
                options={placeOptions}
                getOptionLabel={(option) => option.name}
                // "Marcar todos" só sem busca: com texto digitado ficaria ambíguo (todos ou só os encontrados?)
                filterOptions={(options, state) => {
                  const found = filterPlaces(options, state);
                  return !state.inputValue && found.length > 1 ? [ALL_PLACES, ...found] : found;
                }}
                value={selectedPlaces}
                onChange={(_, newValue, _reason, details) =>
                  handleChange(
                    'stationIds',
                    details?.option === ALL_PLACES
                      ? (allPlacesOn ? [] : placeOptions.map((s) => s.id))
                      : newValue.map((v) => v.id)
                  )
                }
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  const isAll = option === ALL_PLACES;
                  return (
                    <li key={key} {...rest}>
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5, mr: 1 }}
                        checked={isAll ? allPlacesOn : selected}
                        indeterminate={isAll && !allPlacesOn && selectedPlaces.length > 0}
                      />
                      {isAll ? (allPlacesOn ? 'Desmarcar todos' : 'Marcar todos') : option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isAnySaleType ? 'Postos e parceiros' : 'Postos'}
                    error={!!errors.stationIds}
                    helperText={
                      errors.stationIds
                      || (isAnySaleType
                        ? 'Selecione os postos ou parceiros onde a compra vale'
                        : 'Selecione os postos onde esta regra se aplica')
                    }
                  />
                )}
                noOptionsText={isAnySaleType ? 'Nenhum posto ou parceiro encontrado' : 'Nenhum posto encontrado'}
              />
            </Grid>
          )}

          {/* Simulação de alcance da regra.
              Regras de compra ainda não são simuláveis — a gifts-api não tem a
              query de alcance sobre vendas, então mostraria "0 clientes". */}
          {!isClubeCertoType && !isPixPaymentType && !isUniqueCodeType && !isPredictionWinnerType && !isRoletaPrizeType && !isRegistrationType && !isPointsExchangeType && !isAnySaleType && (
            <Grid size={12}>
              <GiftRuleSimulator
                type={formData.type}
                fuelIds={formData.fuelIds || []}
                fuelNames={(formData.fuelIds || []).map(id => fuels.find(f => f.id === id)?.name || id)}
                valueType={formData.valueType}
                value={formData.value}
                quantity={formData.quantity || 1}
                stationIds={formData.stationIds}
              />
            </Grid>
          )}
        </Grid>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        {view === 'location' ? (
          <Button onClick={() => setView('form')} variant="contained" color="primary">
            Concluir localização
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={
                cannotSelectClubeCerto
                || cannotSelectPixPayment
                || cannotAddFillinDueToPixPayment
                || cannotSelectUniqueCode
                || cannotAddOtherDueToUniqueCode
                || cannotSelectPointsExchange
                || cannotAddOtherDueToPointsExchange
                || clubeCertoConfigMissing
              }
            >
              Adicionar Regra
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
